<template>
  <div>
    <add-or-update-country-form
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData"
      @resetUpdateData="resetUpdateData"
    />
    <h1 class="text-center my-2">Countries</h1>

    <b-row class="mb-1">
      <b-col md="4">
        <b-form-group label="RMC" label-for="rmc-filter">
          <v-select
            id="rmc-filter"
            v-model="rmcFilter"
            :loading="rmcLoading"
            :options="rmcOptions"
            :reduce="(val) => val.value"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            placeholder="All RMCs"
          />
        </b-form-group>
      </b-col>
      <b-col md="4" class="d-flex align-items-center">
        <b-form-checkbox v-model="supportedOnly" class="mt-2">
          Active mission countries only
        </b-form-checkbox>
      </b-col>
    </b-row>

    <list-table
      :records="records"
      :columns="tableColumns"
      :total-records="totalRecords"
      :search-filter.sync="search"
      :current-page-number.sync="currentPage"
      :show-search="true"
      :show-filter-by-default="true"
      selection-mode="multi"
      @selected-rows="onRowsSelected"
    >
      <template v-if="can('countries:CanUpdate')" #action_button>
        <b-button
          v-if="selectedCountries.length > 0"
          v-ripple.400="'rgba(40, 199, 111, 0.15)'"
          variant="success"
          @click="bulkActivate"
        >
          <feather-icon icon="CheckCircleIcon" class="mr-50" />
          <span class="align-middle">Activate {{ selectedCountries.length }} Countr{{ selectedCountries.length > 1 ? 'ies' : 'y' }}</span>
        </b-button>
      </template>

      <template #cell(supported)="data">
        <b-badge pill :variant="data.item.supported ? 'light-success' : 'light-secondary'">
          {{ data.item.supported ? 'Active' : 'Inactive' }}
        </b-badge>
      </template>

      <template #cell(rmc)="data">
        {{ data.item.rmc ? data.item.rmc.title : '-' }}
      </template>

      <template #cell(block)="data">
        {{ data.item.block ? data.item.block.title : '-' }}
      </template>

      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item v-if="can('countries:CanUpdate')" @click="onEdit(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ListTable from "@core/components/ListTable/ListTable.vue";
import AddOrUpdateCountryForm from "@/@core/components/shared/geo/AddOrUpdateCountryForm.vue"
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import { debounce, get } from "lodash";
import { BRow, BCol, BButton, BBadge, BFormGroup, BFormCheckbox, BDropdown, BDropdownItem } from "bootstrap-vue";

const watchHandler = {
  handler(){
    this.debounceFetchAllData()
  },
  immediate: false
}

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BBadge,
    vSelect,
    ListTable,
    BFormGroup,
    BFormCheckbox,
    BDropdown,
    BDropdownItem,
    AddOrUpdateCountryForm,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      records: [],
      totalRecords: 0,
      currentPage: 1,

      search: "",
      rmcFilter: null,
      supportedOnly: false,
      rmcLoading: false,
      rmcOptions: [],
      selectedCountries: [],

      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: 'check', label: '', sortable: false },
        { key: 'country', label: "Country", sortable: false },
        { key: 'iso_code', label: "ISO Code", sortable: false },
        { key: 'supported', label: "Status", sortable: false },
        { key: 'rmc', label: "RMC", sortable: false },
        { key: 'block', label: "Mission Bloc", sortable: false },
        { key: 'actions' },
      ]
    }
  },
  watch: {
    search: watchHandler,
    rmcFilter: watchHandler,
    supportedOnly: watchHandler,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);
    this.fetchRmcOptions();
    this.fetchAllData();
  },
  methods: {
    async fetchRmcOptions() {
      try {
        this.rmcLoading = true;

        const request = await this.useJwt().sharedService.fetchRmc({ limit: 500 });
        const { data } = request.data;

        this.rmcOptions = (data || []).map((item) => ({
          label: item.title,
          value: item._id
        }))
      } catch (error) {
        // Silently ignore - the RMC filter is a convenience, not critical path.
      } finally {
        this.rmcLoading = false;
      }
    },
    async fetchAllData() {
      try {
        this.loading = true;

        const query = {
          search: this.search,
        }
        if (this.rmcFilter) {
          query.rmc_id = this.rmcFilter;
        }
        if (this.supportedOnly) {
          query.supported = true;
        }

        const request = await this.useJwt().sharedService.fetchCountries(query);
        const { data } = request.data;

        this.records = data || [];
        this.totalRecords = this.records.length;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onEdit(update){
      this.updateData = update;
      this.isAddOrUpdateActive = true;
    },
    resetUpdateData(){
      if (this.updateData){
        this.updateData = null
      }
    },
    onRowsSelected(selectedRows){
      this.selectedCountries = selectedRows;
    },
    async bulkActivate(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Activation?',
          text: `Do you want to mark ${this.selectedCountries.length} ${this.selectedCountries.length > 1 ? 'countries' : 'country'} as active mission countries?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const ids = this.selectedCountries.map((country) => country._id);
        await this.useJwt().sharedService.bulkUpdateGeoCountriesSupported(ids, true);

        this.selectedCountries = [];
        await this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Countries activated successfully'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
  }
}
</script>
